/**
 * DemoFunnelTile — admin-only widget that surfaces the landing-page demo
 * walkthrough funnel.
 *
 * Pulls /api/landing-demo/summary?days=30 and shows:
 *   - Unique viewers
 *   - Funnel stages (viewed → reached fix act → completed → signed up)
 *   - Set-intersection conversion rates (bounded 0-100%)
 *
 * Drops into ConversionROI so the founder can see "is the walkthrough
 * converting?" in the same screen as feature-level ROI.
 */
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Eye, Play, CheckCircle2, Rocket, BarChart3 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const FunnelStage = ({ icon: Icon, label, count, pctOfPrev, accent }) => (
  <div className="flex items-center justify-between p-3 rounded-lg bg-white border border-slate-200">
    <div className="flex items-center gap-3">
      <div className={`w-9 h-9 rounded-md flex items-center justify-center ${accent}`}>
        <Icon className="w-4 h-4 text-white" />
      </div>
      <div>
        <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">{label}</div>
        <div className="text-2xl font-bold text-slate-900 tabular-nums" data-testid={`funnel-${label.toLowerCase().replace(/\s+/g, '-')}`}>{count}</div>
      </div>
    </div>
    {pctOfPrev !== null && (
      <div className="text-right">
        <div className="text-[9px] text-slate-500 uppercase tracking-widest">→ this stage</div>
        <div className="text-sm font-bold text-emerald-700 tabular-nums">{pctOfPrev}%</div>
      </div>
    )}
  </div>
);

export default function DemoFunnelTile() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [days, setDays] = useState(30);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErr(null);
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API}/api/landing-demo/summary?days=${days}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!cancelled) setData(json);
      } catch (e) {
        if (!cancelled) setErr(String(e.message || e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [days]);

  if (loading) {
    return (
      <Card className="bg-white border-slate-200 shadow-sm" data-testid="demo-funnel-tile-loading">
        <CardContent className="p-6 text-slate-500 text-sm">Loading demo funnel…</CardContent>
      </Card>
    );
  }

  if (err) {
    return (
      <Card className="bg-white border-rose-200 shadow-sm" data-testid="demo-funnel-tile-error">
        <CardContent className="p-6 text-rose-700 text-sm">Demo funnel unavailable — {err}</CardContent>
      </Card>
    );
  }

  const f = data?.funnel || { viewed: 0, reached_fix_act: 0, completed: 0, signed_up: 0 };
  const r = data?.conversion_rates || {};
  const viewToFix = f.viewed > 0 ? Math.round((f.reached_fix_act / f.viewed) * 100) : 0;
  const fixToComplete = f.reached_fix_act > 0 ? Math.round((f.completed / f.reached_fix_act) * 100) : 0;
  const completeToSignup = r.completed_to_signup_pct ?? 0;

  return (
    <Card className="bg-white border-slate-200 shadow-sm" data-testid="demo-funnel-tile">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base text-slate-900 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-emerald-600" />
            Landing demo funnel
            <span className="text-xs text-slate-500 font-normal">(last {days}d)</span>
          </CardTitle>
          <div className="flex gap-1.5">
            {[7, 30, 90].map(d => (
              <button
                key={d}
                onClick={() => setDays(d)}
                className={`px-2.5 py-1 text-[11px] rounded-md font-medium tabular-nums transition-colors ${
                  days === d
                    ? 'bg-emerald-600 text-white shadow-sm shadow-emerald-500/30'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
                data-testid={`funnel-days-${d}`}
              >
                {d}d
              </button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <FunnelStage icon={Eye} label="Viewed" count={f.viewed} pctOfPrev={null} accent="bg-slate-700" />
          <FunnelStage icon={Play} label="Reached fix act" count={f.reached_fix_act} pctOfPrev={viewToFix} accent="bg-cyan-600" />
          <FunnelStage icon={CheckCircle2} label="Completed" count={f.completed} pctOfPrev={fixToComplete} accent="bg-emerald-600" />
          <FunnelStage icon={Rocket} label="Signed up" count={f.signed_up} pctOfPrev={completeToSignup} accent="bg-blue-600" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-3 border-t border-slate-100">
          <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200" data-testid="conv-rate-viewed-to-completed">
            <div className="text-[10px] font-bold text-emerald-700 uppercase tracking-widest">Viewed → Completed</div>
            <div className="text-2xl font-bold text-emerald-900 tabular-nums mt-1">{r.viewed_to_completed_pct ?? 0}<span className="text-base text-emerald-700">%</span></div>
          </div>
          <div className="p-3 rounded-lg bg-blue-50 border border-blue-200" data-testid="conv-rate-viewed-to-signup">
            <div className="text-[10px] font-bold text-blue-700 uppercase tracking-widest">Viewed → Signup</div>
            <div className="text-2xl font-bold text-blue-900 tabular-nums mt-1">{r.viewed_to_signup_pct ?? 0}<span className="text-base text-blue-700">%</span></div>
          </div>
          <div className="p-3 rounded-lg bg-cyan-50 border border-cyan-200" data-testid="conv-rate-completed-to-signup">
            <div className="text-[10px] font-bold text-cyan-700 uppercase tracking-widest">Completed → Signup</div>
            <div className="text-2xl font-bold text-cyan-900 tabular-nums mt-1">{r.completed_to_signup_pct ?? 0}<span className="text-base text-cyan-700">%</span></div>
          </div>
        </div>

        <div className="text-[11px] text-slate-500 italic pt-1">
          {data?.unique_viewers ?? 0} unique viewers · math: <code className="bg-slate-100 px-1 rounded">|stage_X ∩ stage_Y| / |stage_X|</code> · bounded 0–100%
        </div>
      </CardContent>
    </Card>
  );
}
